import { isServer } from '@utils/server'
import { useCallback, useEffect, useMemo, useState } from 'react'

export function useIntersectionObserver(
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit
) {
  const { root, rootMargin, threshold } = options
  return useMemo(
    () =>
      isServer()
        ? undefined
        : new IntersectionObserver(callback, { root, rootMargin, threshold }),
    [callback, root, rootMargin, threshold]
  )
}

export function useVisibilityObserver<T extends HTMLElement = HTMLElement>({
  initiallyVisible = false,
  ...options
}: IntersectionObserverInit & { initiallyVisible?: boolean } = {}) {
  const [element, setElement] = useState<T | null>()
  const [elementVisible, setElementVisible] = useState(!!initiallyVisible)

  const intersectionObserverCallback =
    useCallback<IntersectionObserverCallback>((entries) => {
      entries.forEach((entry) => setElementVisible(entry.isIntersecting))
    }, [])

  const elementObserver = useIntersectionObserver(
    intersectionObserverCallback,
    options
  )

  useEffect(() => {
    if (element) {
      elementObserver?.observe(element)

      return () => elementObserver?.disconnect()
    }
  }, [element, elementObserver])

  return {
    observe: setElement,
    visible: elementVisible,
  }
}
